/* eslint-disable react/no-danger */
import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import {
    DislikeOutlined,
    LikeOutlined,
    PaperClipOutlined,
} from '@ant-design/icons';
import {
    Button,
    Checkbox,
    CheckboxProps,
    Grid,
    Modal,
    Space,
    Typography,
    message,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { AnnouncementData } from '../../../store/Announcement/types';
import { useAuth } from '../../../contexts/Auth';
import RemoveBorderFooter, {
    ContentFooter,
    ContentModal,
    ContentTitle,
    TextData,
    TextTitle,
    BackgroundTransparent,
    AreaContent,
    CardContent,
    ButtonDelete,
    TitleResolved,
    Header,
    HeaderText,
} from './styles';
import { useUpdateAnnouncement } from '../../../store/Announcement/ShowAnnouncement/shopmanager';
import { useDeleteAnnouncement } from '../../../store/Announcement/DeleteAnnouncement';
import { EnRoles } from '../../../interfaces/Roles';
import { ConfirmModal } from '../../Layout';
import {
    RequestReaction,
    useCreateReactAnnouncementMutation,
} from '../../../store/Announcement/Reaction';
import { useChangeStatusResolved } from '../../../store/Announcement/Resolved';

interface Props {
    dataModal: AnnouncementData;
    openModal: React.Dispatch<React.SetStateAction<boolean>>;
    onClose?: () => void;
    onSkip?: () => void;
    current?: number;
    total?: number;
}

type Reaction = 'LIKE' | 'DISLIKE' | null;

export const PopUpAnnouncement: React.FC<Props> = ({
    dataModal,
    openModal,
    onClose,
    onSkip,
    current = 0,
    total = 1,
}) => {
    const { mutateAsync } = useUpdateAnnouncement();
    const { mutateAsync: mutateAsyncReact } =
        useCreateReactAnnouncementMutation();
    const { mutateAsync: mutateAsyncChangeStatusResolved } =
        useChangeStatusResolved();
    const [openDelete, setOpenDelete] = useState<boolean>(false);
    const { data: authData, isAssuming } = useAuth();
    const screens = Grid.useBreakpoint();
    const isMobile = screens.md === false;

    const navigate = useNavigate();

    const [resolved, setResolved] = useState(
        !dataModal?.recipients ? false : !!dataModal.recipients[0]?.resolved_at,
    );
    const {
        title,
        content,
        created_at: date,
        link,
        attachments,
        id,
        isPreview,
        recipients,
    } = dataModal;

    const [reaction, setReaction] = useState<RequestReaction>({
        internal_notification_id: String(id ?? ''),
        reaction: recipients?.length ? recipients[0].reaction : 'DISLIKE',
    });

    const handleReaction = async (newReaction: Reaction) => {
        try {
            await mutateAsyncReact({
                ...reaction,
                reaction: newReaction,
            });
            setReaction({
                ...reaction,
                reaction: newReaction,
            });
        } catch (error) {
            // erro
        }
    };

    const isShopManager = authData?.role.some(
        (role: { type: string }) => role.type === EnRoles.shopManager,
    );

    const isAdmin = authData?.role.some(
        (role: { type: string }) => role.type === EnRoles.sysAdmin,
    );

    const {
        mutateAsync: deleteMutate,
        isLoading: deleteLoading,
        isError,
    } = useDeleteAnnouncement();

    const data = dayjs(date).format('DD/MM/YY - HH:mm');
    const shortcut = link ? ` Link: ${link}` : '';
    const remainingAnnouncements = total - current;

    const { Link } = Typography;

    const onConfirmModal = React.useCallback(() => {
        if (onClose) {
            onClose();
        } else {
            if (isShopManager && !isAssuming) {
                mutateAsync({ id: String(dataModal.id) });
            }
            openModal(false);
        }
    }, [dataModal, onClose]);

    const deleteAnnouncement = async () => {
        if (!isAdmin) {
            message.error(
                'Desculpe, você não possui permissão para excluir este comunicado.',
            );
            return;
        }

        await deleteMutate({ id: String(id) });
        openModal(false);
        message.success('Comunicado deletado com sucesso!');
    };

    const onChangeAnnouncementResolved: CheckboxProps['onChange'] = async (
        e,
    ) => {
        try {
            await mutateAsyncChangeStatusResolved({
                internal_notification_id: String(id || ''),
                resolved: e.target.checked,
            });
            setResolved(e.target.checked);
        } catch {
            // nenhum erro
        }
    };

    useEffect(() => {
        if (isAdmin && !isPreview && isError) {
            message.error(
                'Ocorreu um erro ao tentar excluir o comunicado. Por favor, tente novamente mais tarde.',
            );
        }
    }, [isError, isAdmin, isPreview]);

    const { innerHeight } = window;

    const mobileButtonStyle: React.CSSProperties = {
        width: '100%',
        marginInlineStart: 0,
    };
    const actionButtonStyle: React.CSSProperties = isMobile
        ? mobileButtonStyle
        : { padding: '0 100px', maxWidth: 228 };

    const handleSkip = React.useCallback(() => {
        const skipUntil = new Date();
        skipUntil.setHours(skipUntil.getHours() + 24);
        localStorage.setItem(
            '@NeerHouse:skipAnnouncementsUntil',
            skipUntil.getTime().toString(),
        );
        openModal(false);
    }, []);

    return (
        <>
            <RemoveBorderFooter />
            <Modal
                centered={!isMobile}
                open
                closable={false}
                onOk={onConfirmModal}
                onCancel={onConfirmModal}
                width={isMobile ? '100%' : undefined}
                styles={
                    isMobile
                        ? {
                              container: {
                                  height: '100dvh',
                                  padding: 0,
                                  borderRadius: 0,
                                  display: 'flex',
                                  flexDirection: 'column',
                              },
                              body: {
                                  flex: 1,
                                  minHeight: 0,
                                  padding: 0,
                                  display: 'flex',
                                  flexDirection: 'column',
                              },
                              footer: {
                                  margin: 0,
                                  padding:
                                      '12px 16px calc(22px + env(safe-area-inset-bottom))',
                              },
                          }
                        : {
                              body: {
                                  height: innerHeight - 150,
                                  padding: '0',
                                  display: 'flex',
                                  flexDirection: 'column',
                              },
                          }
                }
                style={
                    isMobile
                        ? {
                              top: 0,
                              margin: 0,
                              maxWidth: '100vw',
                              paddingBottom: 0,
                          }
                        : { minWidth: '92vw' }
                }
                cancelText
                okText
                footer={[
                    <div
                        style={{
                            width: '100%',
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            alignItems: isMobile ? 'stretch' : 'center',
                            justifyContent: 'space-between',
                            gap: isMobile ? 12 : 24,
                            position: 'relative',
                        }}
                    >
                        {isShopManager && !isAssuming && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: isMobile
                                        ? 'center'
                                        : undefined,
                                    gap: 8,
                                }}
                            >
                                <Button
                                    type="text"
                                    size="large"
                                    shape="circle"
                                    onClick={() =>
                                        handleReaction(
                                            reaction.reaction !== 'LIKE'
                                                ? 'LIKE'
                                                : null,
                                        )
                                    }
                                    style={{
                                        backgroundColor: 'transparent',
                                        color:
                                            reaction.reaction === 'LIKE'
                                                ? '#20AC54'
                                                : 'gray',
                                        marginRight: 15,
                                    }}
                                    icon={
                                        <LikeOutlined
                                            style={{
                                                color:
                                                    reaction.reaction === 'LIKE'
                                                        ? '#20AC54'
                                                        : 'gray',
                                            }}
                                        />
                                    }
                                >
                                    Gostei
                                </Button>
                                <Button
                                    type="text"
                                    size="large"
                                    shape="circle"
                                    onClick={() =>
                                        handleReaction(
                                            reaction.reaction !== 'DISLIKE'
                                                ? 'DISLIKE'
                                                : null,
                                        )
                                    }
                                    style={{
                                        backgroundColor: 'transparent',
                                        color:
                                            reaction.reaction === 'DISLIKE'
                                                ? '#FF0000'
                                                : 'gray',
                                        marginLeft: 15,
                                    }}
                                    icon={
                                        <DislikeOutlined
                                            style={{
                                                color:
                                                    reaction.reaction ===
                                                    'DISLIKE'
                                                        ? '#FF0000'
                                                        : 'gray',
                                            }}
                                        />
                                    }
                                >
                                    Não gostei
                                </Button>
                            </div>
                        )}
                        <div
                            style={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                gap: 8,
                                flexWrap: isMobile ? 'nowrap' : 'wrap',
                                justifyContent: 'flex-end',
                            }}
                        >
                            {isAdmin && !isPreview && (
                                <ButtonDelete
                                    danger
                                    type="default"
                                    onClick={() => setOpenDelete(true)}
                                    disabled={deleteLoading}
                                    block={isMobile}
                                    style={
                                        isMobile
                                            ? {
                                                  ...mobileButtonStyle,
                                                  padding: 0,
                                              }
                                            : { maxWidth: 228 }
                                    }
                                />
                            )}
                            {isShopManager && !isAssuming && (
                                <Button
                                    type="default"
                                    onClick={() => {
                                        if (onSkip) {
                                            onSkip();
                                        } else {
                                            handleSkip();
                                        }
                                    }}
                                    block
                                    style={{
                                        ...(isMobile
                                            ? mobileButtonStyle
                                            : { maxWidth: 228 }),
                                        padding: '0',
                                        color: '#1890ff',
                                    }}
                                >
                                    Ler depois ({remainingAnnouncements}{' '}
                                    comunicado
                                    {remainingAnnouncements > 1 ? 's' : ''})
                                </Button>
                            )}
                            {isAdmin && !dataModal.isPreview && (
                                <Button
                                    type="primary"
                                    onClick={() =>
                                        navigate(
                                            `/announcements/edit/${dataModal.id}`,
                                        )
                                    }
                                    block
                                    style={actionButtonStyle}
                                >
                                    Editar
                                </Button>
                            )}
                            <Button
                                type="primary"
                                onClick={onConfirmModal}
                                block
                                style={actionButtonStyle}
                            >
                                Fechar
                            </Button>
                        </div>
                    </div>,
                ]}
            >
                <Header>
                    <HeaderText>
                        Comunicados ({current + 1} de {total})
                    </HeaderText>
                </Header>
                <ContentModal>
                    <ContentTitle>
                        <TextData style={{ fontSize: isMobile ? 14 : 16 }}>
                            {data}
                        </TextData>
                        <TextTitle
                            title={title}
                            ellipsis={{ rows: 2 }}
                            style={{ fontSize: isMobile ? 20 : 24, margin: 0 }}
                        >
                            {title}
                        </TextTitle>
                    </ContentTitle>
                    <AreaContent>
                        <CardContent
                            id="content"
                            style={{
                                textAlign: 'justify',
                                position: 'relative',
                                overflowY: 'auto',
                                height: '100%',
                            }}
                        >
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: content ?? '',
                                }}
                            />
                        </CardContent>
                        <BackgroundTransparent />
                    </AreaContent>
                    <ContentFooter>
                        <Space orientation="vertical" style={{ width: '100%' }}>
                            <Link
                                href={shortcut.replace('Link:', '').trim()}
                                target="_blank"
                                style={{
                                    textDecorationLine: 'underline',
                                    fontSize: 16,
                                }}
                            >
                                {shortcut}
                            </Link>
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 8,
                                    alignItems: 'center',
                                    justifyContent:
                                        attachments && attachments?.length > 0
                                            ? 'space-between'
                                            : 'flex-end',
                                }}
                            >
                                <Space
                                    style={{
                                        display: 'flex',
                                        maxWidth: isMobile
                                            ? '100%'
                                            : isShopManager
                                            ? '70vw'
                                            : '85vw',
                                        overflowX: 'auto',
                                        flexDirection: 'row',
                                        whiteSpace: 'nowrap',
                                        marginTop: 4,
                                    }}
                                >
                                    {attachments?.map((el, indice) => {
                                        return (
                                            <Button
                                                type="link"
                                                icon={<PaperClipOutlined />}
                                                style={{
                                                    padding: 0,
                                                    color: 'gray',
                                                }}
                                                href={el.url}
                                                key={indice}
                                            >
                                                {el.name}
                                            </Button>
                                        );
                                    })}
                                </Space>
                                {isShopManager && !isAssuming && (
                                    <div
                                        style={{
                                            display: 'flex',
                                        }}
                                    >
                                        <TitleResolved>
                                            Marcar como resolvido?
                                        </TitleResolved>
                                        <Checkbox
                                            value={resolved}
                                            defaultChecked={resolved}
                                            style={{ width: 29, height: 29 }}
                                            onChange={
                                                onChangeAnnouncementResolved
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </Space>
                    </ContentFooter>
                </ContentModal>
            </Modal>

            <ConfirmModal
                title="Excluir comunicado"
                visible={openDelete}
                onOk={deleteAnnouncement}
                onCancel={() => setOpenDelete(false)}
                okButtonProps={{ loading: deleteLoading }}
            />
        </>
    );
};
